import { fetchJsonRobust } from './fetch'
import { titleLooksUninteresting } from '../../utils/text'
import { NATURE_KEYWORDS } from '../../utils/constants'

/**
 * Fetch Wikidata description for a QID
 */
export async function fetchWikidataDesc(
  qid: string,
  lang: string
): Promise<{ title: string; extract: string } | null> {
  const url = `https://www.wikidata.org/wiki/Special:EntityData/${qid}.json`
  const data = await fetchJsonRobust(url)
  const ent = data?.entities?.[qid]
  if (!ent) return null

  const label = ent.labels?.[lang]?.value || ent.labels?.en?.value || null
  const desc =
    ent.descriptions?.[lang]?.value || ent.descriptions?.en?.value || null

  if (!label && !desc) return null
  return { title: label || qid, extract: desc || '' }
}

/**
 * Fetch Wikidata items around a location (nature-focused)
 */
export async function fetchWikidataAroundNature(
  lat: number,
  lon: number,
  radiusMeters: number,
  lang: string
): Promise<
  Array<{
    source: 'wdnature'
    key: string
    title: string
    lat: number
    lon: number
    desc: string
  }>
> {
  const radiusKm = Math.max(0.1, Math.min(10, radiusMeters / 1000))
  const langClause = `${lang},en`
  const sparql = `
SELECT ?item ?itemLabel ?itemDescription ?lat ?lon WHERE {
  SERVICE wikibase:around {
    ?item wdt:P625 ?loc .
    bd:serviceParam wikibase:center "Point(${lon} ${lat})"^^geo:wktLiteral .
    bd:serviceParam wikibase:radius "${radiusKm}" .
  }
  BIND(geof:latitude(?loc) AS ?lat) .
  BIND(geof:longitude(?loc) AS ?lon) .
  SERVICE wikibase:label { bd:serviceParam wikibase:language "${langClause}". }
}
LIMIT 20`.trim()

  const url =
    'https://query.wikidata.org/sparql?format=json&query=' +
    encodeURIComponent(sparql)

  const data = await fetchJsonRobust(
    url,
    { headers: { Accept: 'application/sparql-results+json' } },
    { timeoutMs: 9000, retries: 1 }
  )

  const bindings = data?.results?.bindings || []
  const out: Array<{
    source: 'wdnature'
    key: string
    title: string
    lat: number
    lon: number
    desc: string
  }> = []

  for (const b of bindings) {
    const label = b.itemLabel?.value || null
    const desc = b.itemDescription?.value || ''
    const la = Number(b.lat?.value)
    const lo = Number(b.lon?.value)

    if (!label || !Number.isFinite(la) || !Number.isFinite(lo)) continue
    if (titleLooksUninteresting(label)) continue

    const blob = (label + ' ' + desc).toLowerCase()
    if (!NATURE_KEYWORDS.some((w) => blob.includes(w))) continue

    out.push({
      source: 'wdnature',
      key: 'wdn:' + (b.item?.value || label),
      title: label,
      lat: la,
      lon: lo,
      desc,
    })
  }

  return out
}
